<template>
	<div v-if="person">
		<v-row>
			<!-- firstName -->
			<v-col cols="6" md="4">
				<v-text-field
					solo
					v-model="person.firstName"
					:disabled="disableEdit"
					filled
					outlined
					dense
					label="Nombre (Obligatorio)"
					:error-messages="nameErrors"
					@input="$v.person.firstName.$touch()"
					@blur="$v.person.firstName.$touch()"
				/>
			</v-col>
			<!-- lastName -->
			<v-col cols="6" md="4">
				<v-text-field
					solo
					v-model="person.lastName"
					:disabled="disableEdit"
					filled
					hide-details
					outlined
					dense
					label="Apellido"
					:required="false"
				></v-text-field>
			</v-col>
			<!-- rut -->
			<v-col cols="6" md="4">
				<v-text-field
					solo
					v-model="person.rut"
					:disabled="disableEdit"
					outlined
					dense
					filled
					label="Rut"
					:required="false"
				></v-text-field>
			</v-col>
			<!-- birthdate -->
			<v-col cols="6" md="4">
				<v-menu
					ref="menu"
					v-model="menu"
					:close-on-content-click="false"
					:return-value.sync="person.birthdate"
					transition="scale-transition"
					offset-y
					min-width="290px"
				>
					<template v-slot:activator="{ on }">
						<v-text-field
							solo
							v-model="person.birthdate"
							label="Fecha de nacimiento"
							:disabled="disableEdit"
							readonly
							outlined
							dense
							filled
							hide-details
							v-on="on"
						></v-text-field>
					</template>
					<v-date-picker
						v-model="person.birthdate"
						:disabled="disableEdit"
						no-title
						scrollable
						locale="es"
						:show-current="false"
						:required="false"
					>
						<v-spacer></v-spacer>
						<v-btn text color="primary" @click="menu = false">Cerrar</v-btn>
						<v-btn text color="primary" @click="$refs.menu.save(person.birthdate)">
							ok
						</v-btn>
					</v-date-picker>
				</v-menu>
			</v-col>
			<!-- state -->
			<v-col cols="6" md="4">
				<v-text-field
					solo
					v-model.trim="person.state"
					:disabled="disableEdit"
					outlined
					dense
					filled
					type="text"
					label="Estado civil"
				/>
			</v-col>
			<!-- phone -->
			<v-col cols="6" md="4">
				<v-text-field
					solo
					v-model="$v.person.phone.$model"
					:disabled="disableEdit"
					outlined
					dense
					filled
					label="Teléfono 8 dígitos"
					placeholder="+569 xxxxyyyy"
					:error-messages="phoneErrors"
					@input="$v.person.phone.$touch()"
					@blur="$v.person.phone.$touch()"
				></v-text-field>
			</v-col>
			<!-- email -->
			<v-col cols="12" md="8">
				<v-text-field
					solo
					v-model.trim="person.email"
					outlined
					dense
					:disabled="!create || disableEdit"
					filled
					type="email"
					label="Correo electronico"
					hint="Una vez convertido, no puede modificar el correo"
					:persistent-hint="personProps.email && create"
					><template v-slot:append>
						<user-convert-button
							v-if="personProps.email && create"
							:person-id="person._id"
							:disabled="isInvite || disabledConvert"
						/>
					</template>
				</v-text-field>
			</v-col>
		</v-row>
		<v-row>
			<v-col cols="12" v-if="personProps.phone && personProps.phone.length === 8">
				<phone-confirm-status class="mt-2" :is-phone-verified="person.isPhoneVerified" />
				<phone-confirm-button
					v-if="person.isPhoneVerified !== 2"
					:disabled="
						phoneErrors.length > 0 || !person.phone || person.isPhoneVerified === 2
					"
					:is-phone-verified="person.isPhoneVerified"
					:person-id="person._id"
				/>
			</v-col>
			<!-- save -->
			<v-col cols="12">
				<v-btn
					block
					small
					color="success"
					:loading="loading"
					:disabled="!hasChangeForm"
					@click="onSubmit"
				>
					Guardar
				</v-btn>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, minLength, maxLength, numeric } from 'vuelidate/lib/validators';
import { isEqual, cloneDeep } from 'lodash';

export default {
	name: 'PersonalData',
	components: {
		PhoneConfirmStatus: () => import('@/components/persons/PhoneConfirmStatus'),
		PhoneConfirmButton: () => import('@/components/persons/PhoneConfirmButton'),
		UserConvertButton: () => import('@/components/persons/UserConvertButton'),
	},
	mixins: [validationMixin],
	props: {
		disableEdit: { type: Boolean, default: false },
		isInvite: {
			type: Boolean,
			default: false,
		},
		create: {
			type: Boolean,
			default: false,
		},
		personProps: {
			type: Object,
			required: true,
		},
		editMode: {
			type: Boolean,
			default: false,
		},
		isUser: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			person: null,
			loading: false,
			menu: false,
		};
	},
	computed: {
		// emailErrors() {
		// 	const errors = [];
		// 	if (!this.$v.person.email.$dirty) return errors;
		// 	!this.$v.person.email.email && errors.push('Escriba un email valido');
		// 	return errors;
		// },
		phoneErrors() {
			const errors = [];
			if (!this.$v.person.phone.$dirty) return errors;
			if (this.$v.person.phone.minLength && !this.$v.person.phone.maxLength)
				errors.push('Máximo 8 números');
			if (this.$v.person.phone.maxLength && !this.$v.person.phone.minLength)
				errors.push('Minimo 8 números');
			if (!this.$v.person.phone.numeric) errors.push('Sólo números');
			return errors;
		},
		nameErrors() {
			const errors = [];
			if (!this.$v.person.firstName.$dirty) return errors;
			!this.$v.person.firstName.required && errors.push('El nombre es querido');
			return errors;
		},
		hasChangeForm() {
			return !isEqual(this.personProps, this.person);
		},
		disabledConvert() {
			return !this.person.email ||
				(this.person.isUser && this.person.idCompany == this.userProfile._id) ||
				this.person.sharedUsers
				? this.person.sharedUsers.some(share => share == this.userProfile._id)
				: false;
		},
	},
	created() {
		this.person = cloneDeep(this.personProps);
	},
	methods: {
		async onSubmit() {
			this.$v.$touch();
			if (!this.$v.$invalid && this.hasChangeForm) {
				this.loading = true;
				await this.putPerson({ ...this.person });
				this.loading = false;
			}
		},
		...mapActions({
			putPerson: 'Person/putPerson',
		}),
	},
	validations: {
		person: {
			firstName: {
				required,
			},
			phone: {
				minLength: minLength(8),
				maxLength: maxLength(8),
				numeric,
			},
			// email: { email },
		},
	},
	watch: {
		personProps() {
			this.person = cloneDeep(this.personProps);
		},
	},
};
</script>
